import { MenuItem, Stack } from '@mui/material';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Sector } from '@/domain/impact/impact.entity';
import {
  AccommodationTypeValues,
  Category,
  ImpactCo2Answer,
  Source,
  ValuesAccommodationPublic,
} from '@/domain/impact-co2/entities/question.entity';
import { selectImpactCo2Answers } from '@/domain/impact-co2/impact-co2.selectors';
import { FSPrimaryButton } from '@/ui/components/button/FSPrimaryButton';
import { FSSecondaryButton } from '@/ui/components/button/FSSecondaryButton';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import {
  AddNewQuiz,
  NewQuizEdition,
  QuizEdition,
  QuizNotEdition,
} from '@/ui/pages/app/components/quiz-panel/co2/components/QuizComponents';
import { isNumber } from '@/ui/utils/form-validations';

import { ImpactQuestionContext } from '../../../ImpactQuestionContext';
import { useImpactCo2AnswerSubmitDisabled } from '../../hooks/useImpactCo2AnswerSubmitDisabled';

const CATEGORY: Category = 'ACCOMMODATION_PUBLIC';

export function AccommodationPublicForm({
  sector,
  onSubmit,
  onDelete,
  onEdit,
  source = '',
  isExternal = false,
}: {
  sector: Sector;
  onSubmit(answer: ImpactCo2Answer): void;
  onDelete(answer: ImpactCo2Answer): void;
  onEdit(answer: ImpactCo2Answer): void;
  isExternal?: boolean;
  source?: Source;
}) {
  const { hideCompleted, panelOpen, answersSelectorOverride } = useContext(
    ImpactQuestionContext,
  );
  const [displayNew, setDisplayNew] = useState(false);
  const [editing, setEditing] = useState<Array<number>>([]);

  const defaultAnswersSelector =
    selectImpactCo2Answers<ValuesAccommodationPublic>({
      sector: sector,
      category: CATEGORY,
      isExternal: isExternal,
    });
  let answersSelector = defaultAnswersSelector;
  if (answersSelectorOverride) {
    answersSelector = answersSelectorOverride;
  }
  const answers: ImpactCo2Answer<ValuesAccommodationPublic>[] =
    useAppSelector(answersSelector);

  const { disableSubmitActions } = useImpactCo2AnswerSubmitDisabled();

  const handleSubmit = (values: ValuesAccommodationPublic) => {
    const answer: ImpactCo2Answer = {
      id: -1,
      userId: '',
      sector: sector,
      category: CATEGORY,
      values: values,
      isExternal: isExternal,
      source: source,
    };
    onSubmit(answer);
    setDisplayNew(false);
  };

  const handleEdit = (values: ValuesAccommodationPublic, id: number) => {
    const answer: ImpactCo2Answer = {
      id,
      userId: '',
      sector,
      category: CATEGORY,
      values,
      isExternal: isExternal,
      source: source,
    };
    onEdit(answer);
    removeFromEditing(answer.id);
  };

  const handleDelete = () => {
    setDisplayNew(false);
  };

  const addToEditing = (id: number) => {
    setEditing([...editing, id]);
  };

  const removeFromEditing = (id: number) => {
    setEditing([...editing].filter((i) => i !== id));
  };

  return (
    <>
      {/* check if we have to hide question */}
      {!(hideCompleted && answers.length > 0) && (
        <>
          {/* already created answers */}
          {answers
            .sort((a, b) => a.id - b.id)
            .map((answer, i) => (
              <div key={`accommodation-public` + i}>
                {editing.includes(answer.id) && !!panelOpen ? (
                  <QuizEdition
                    answer={answer}
                    removeFromEditing={removeFromEditing}
                    index={i}
                    title={translate(
                      'ImpactQuestion.ACCOMMODATION.question-title',
                    )}
                    form={
                      <Form
                        onSubmit={(values) => handleEdit(values, answer.id)}
                        defaultValues={answer.values}
                        onDelete={() => onDelete(answer)}
                        answersCount={answers.length}
                        disableSubmit={disableSubmitActions}
                        disableDelete={disableSubmitActions}
                      />
                    }
                  />
                ) : (
                  <QuizNotEdition
                    addToEditing={addToEditing}
                    answeredValues={[
                      translate(
                        `ImpactQuestion.ACCOMMODATION.accommodationTypeValues.${answer.values.accommodationType}`,
                      ),
                      `${answer.values.numberOfNights} ${translate(
                        'ImpactQuestion.ACCOMMODATION.nigths',
                      )}`,
                      `${answer.values.guestCount}  ${translate(
                        answer.values.guestCount > 1
                          ? 'ImpactQuestion.ACCOMMODATION_PUBLIC.people'
                          : 'ImpactQuestion.ACCOMMODATION_PUBLIC.person',
                      )}`,
                    ]}
                    title={translate(
                      'ImpactQuestion.ACCOMMODATION.question-title',
                    )}
                    index={i}
                    answer={answer}
                  />
                )}
              </div>
            ))}

          {/* new answer */}
          {displayNew || answers.length === 0 ? (
            <NewQuizEdition
              answersLength={answers.length}
              title={translate('ImpactQuestion.ACCOMMODATION.question-title')}
              form={
                <Form
                  onSubmit={handleSubmit}
                  onDelete={() => handleDelete()}
                  answersCount={answers.length}
                  disableSubmit={disableSubmitActions}
                  disableDelete={disableSubmitActions}
                />
              }
            />
          ) : (
            <AddNewQuiz
              setDisplayNew={setDisplayNew}
              buttonTitle={translate(
                'ImpactQuestion.ACCOMMODATION.add-button-title',
              )}
            />
          )}
        </>
      )}
    </>
  );
}

function Form({
  onSubmit,
  onDelete,
  defaultValues,
  answersCount,
  disableSubmit,
  disableDelete,
}: {
  onSubmit: (values: ValuesAccommodationPublic) => void;
  onDelete: () => void;
  defaultValues?: ValuesAccommodationPublic;
  answersCount: number;
  disableSubmit: boolean;
  disableDelete: boolean;
}) {
  const initialValues: ValuesAccommodationPublic = defaultValues || {
    accommodationType: 'HOTEL',
    numberOfNights: 1,
    guestCount: 1,
    tags: [],
  };

  const { handleSubmit, register, formState } =
    useForm<ValuesAccommodationPublic>({
      defaultValues: initialValues,
    });

  const { errors } = formState;

  const handleFormSubmit = (data: ValuesAccommodationPublic) => {
    const values = {
      ...data,
      numberOfNights: parseInt(data.numberOfNights.toString()),
      guestCount: parseInt(data.guestCount.toString()),
    };
    onSubmit(values);
  };

  return (
    <Stack>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack>
          <FSTextField
            select
            defaultValue={initialValues.accommodationType}
            label={translate(
              'ImpactQuestion.ACCOMMODATION.accommodationTypeTitle',
            )}
            register={{
              ...register('accommodationType'),
            }}
            error={!!errors.accommodationType}
            helperText={errors.accommodationType?.message}
          >
            {AccommodationTypeValues.map((value) => (
              <MenuItem key={value} value={value}>
                {translate(
                  `ImpactQuestion.ACCOMMODATION.accommodationTypeValues.${value}`,
                )}
              </MenuItem>
            ))}
          </FSTextField>

          <FSTextField
            type="number"
            label={translate(
              'ImpactQuestion.ACCOMMODATION.numberOfNightsTitle',
            )}
            defaultValue={initialValues.numberOfNights}
            register={{
              ...register('numberOfNights', {
                required: translate('Form.fill-the-field'),
                validate: isNumber,
              }),
            }}
            error={!!errors.numberOfNights}
            helperText={errors.numberOfNights?.message}
          />

          <FSTextField
            type="number"
            label={translate(
              'ImpactQuestion.ACCOMMODATION_PUBLIC.guestCountTitle',
            )}
            defaultValue={initialValues.guestCount}
            register={{
              ...register('guestCount', {
                required: translate('Form.fill-the-field'),
                validate: isNumber,
              }),
            }}
            error={!!errors.guestCount}
            helperText={errors.guestCount?.message}
          />

          <Stack direction={'row'} marginTop={1} gap={1}>
            <FSPrimaryButton type="submit" disabled={disableSubmit}>
              {translate(defaultValues ? 'Form.save' : 'Form.add')}
            </FSPrimaryButton>
            {onDelete && answersCount > 0 && (
              <FSSecondaryButton onClick={onDelete} disabled={disableDelete}>
                {translate('Form.delete')}
              </FSSecondaryButton>
            )}
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
}
